import React, { useRef, useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch, RootStateOrAny } from "react-redux";
import { Form } from "@unform/web";
import { FormHandles, SubmitHandler } from "@unform/core";
import { OptionTypeBase } from "react-select";
import * as Yup from "yup";
import ReactGA from "react-ga";
import ReactPixel from "react-facebook-pixel";
import { useHistory } from "react-router-dom";
import { useValidation } from "hooks";
import { v4 as uuidV4 } from "uuid";
import { statesOptions } from "utils";
import { Braces, Game, Treatments } from "components/Home";
import { FetchTagIndexActions } from "store/ducks/unidades";
import { Scaffold, UnitCard, Footer } from "components/Shared";
import { Select, Input, InputMask } from "components/Shared/Form";
import {
  BookingIdentUserActions,
  ChkCpfActions,
  iChkCpf,
} from "store/ducks/booking";
import {
  GeolocationUnidadeActions,
  ListUnidadesActions, ListUnidadesState
} from "store/ducks/unidades";

import boneco from "assets/images/thop-apontando.png";
import * as S from "./styles";



export const BookingIdentUser: React.FC = () => {
  const dispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [isWaitingForGeocode, setIsWaitingForGeocode] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [selectedState, setSelectedState] = useState<OptionTypeBase | null>(
    null,
  );
  const [cityOptions, setCityOptions] = useState<OptionTypeBase[]>([]);

     useEffect(() => {
    dispatch(FetchTagIndexActions.request("general"));
  }, [dispatch]);


  const history = useHistory();

  const { handleFormErrors } = useValidation();
  const lastCheckedCpf = useRef("");
  const bookingIdUser = useSelector(
    (state: RootStateOrAny) => state.bookingIdentUser.data[0],
  );
  const geolocation = useSelector(
    (state: RootStateOrAny) => state.geolocationUnidade,
  );
  const { data: listUnitsData, loading: listUnitsLoading } = useSelector<
    RootStateOrAny,
    ListUnidadesState
  >((state) => state.listUnidades);

  const skippedInitialUnitsLookup = useRef(false);

  useEffect(() => {
    dispatch(BookingIdentUserActions.reset());
    dispatch(ListUnidadesActions.reset());
  }, [dispatch]);

  useEffect(() => {
    if (!skippedInitialUnitsLookup.current) {
      skippedInitialUnitsLookup.current = true;
      return;
    }

    const city = bookingIdUser?.cidade?.trim();
    const state = bookingIdUser?.estado?.trim();

    if (!city || !state) return;

    dispatch(ListUnidadesActions.request({ state, cidade: city }));
  }, [bookingIdUser?.cidade, bookingIdUser?.estado, dispatch]);

  const handleCpfChange = useCallback(
    (cpfValue: string) => {
      const cpf = cpfValue.replace(/\D/g, "");
      if (cpf.length !== 11) {
        lastCheckedCpf.current = "";
        return;
      }

      if (lastCheckedCpf.current === cpf) return;

      lastCheckedCpf.current = cpf;
      dispatch(
        ChkCpfActions.request(
          cpf,
          (nome: string) => {
            if (lastCheckedCpf.current === cpf) {
              formRef.current?.setFieldValue("nome", nome);
            }
          },
          () => {
            if (lastCheckedCpf.current !== cpf) return;

            formRef.current?.setFieldValue("cpf", "");
            formRef.current?.setFieldValue("nome", "");
            lastCheckedCpf.current = "";
          },
        ),
      );
    },
    [dispatch],
  );

  const hasCompleteUserData = [
    bookingIdUser?.nome,
    bookingIdUser?.cpf,
    bookingIdUser?.celular,
    bookingIdUser?.email,
  ].every((value) => typeof value === "string" && value.trim() !== "");

  const handleAllowLocation = useCallback(() => {
    setLocationError(null);

    if (bookingIdUser) {
      dispatch(
        BookingIdentUserActions.setData([
          {
            ...bookingIdUser,
            allowLocation: true,
            action: true,
          },
        ]),
      );
    }

    if (!navigator.geolocation) {
      setLocationError("Este navegador não permite obter a localização.");
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        dispatch(
          GeolocationUnidadeActions.request({
            latlng: `${coords.latitude},${coords.longitude}`,
          }),
        );
        setIsWaitingForGeocode(true);
      },
      (error) => {
        setLocationError(error.message);
        setIsLocating(false);
      },
    );
  }, [bookingIdUser, dispatch]);

  const handleDenyLocation = useCallback(() => {
    if (!bookingIdUser) return;

    dispatch(
      BookingIdentUserActions.setData([
        {
          ...bookingIdUser,
          action: true,
        },
      ]),
    );
  }, [bookingIdUser, dispatch]);

  useEffect(() => {
    if (!isWaitingForGeocode || geolocation.loading) return;

    if (geolocation.error) {
      setLocationError(geolocation.error);
      setIsWaitingForGeocode(false);
      setIsLocating(false);
      return;
    }

    setIsWaitingForGeocode(false);
    setIsLocating(false);

    if (!geolocation.data) {
      setLocationError("Não foi possível obter o endereço da sua localização.");
      return;
    }

    const response = geolocation.data;
    const addressComponents = response.results?.[0]?.address_components;
    const cityComponent =
      addressComponents?.find((component: { types: string[] }) =>
        component.types.includes("administrative_area_level_2"),
      ) ||
      addressComponents?.find((component: { types: string[] }) =>
        component.types.includes("locality"),
      );
    const stateComponent = addressComponents?.find(
      (component: { types: string[] }) =>
        component.types.includes("administrative_area_level_1"),
    );
    // const city = cityComponent?.long_name;
    // const state = stateComponent?.short_name;

    const city = cityComponent?.long_name
  ? normalizeLocation(cityComponent.long_name)
  : "";

const state = stateComponent?.short_name
  ? normalizeLocation(stateComponent.short_name)
  : "";

    if (response.status !== "OK" || !city || !state) {
      setLocationError("Não foi possível identificar a cidade e o estado.");
      return;
    }

    if (!bookingIdUser) {
      setLocationError("Não foi possível encontrar os dados do usuário.");
      return;
    }

    dispatch(
      BookingIdentUserActions.setData([
        {
          ...bookingIdUser,
          cidade: city,
          estado: state,
          idUnidade: null,
        },
      ]),
    );
  }, [
    bookingIdUser,
    dispatch,
    geolocation.data,
    geolocation.error,
    geolocation.loading,
    isWaitingForGeocode,
  ]);

  useEffect(() => {
    if (!selectedState) return;

    dispatch(ListUnidadesActions.request({ state: selectedState.value }));
  }, [dispatch, selectedState]);

  useEffect(() => {
    if (!selectedState) {
      setCityOptions([]);
      return;
    }

    const cities = Array.from(
      new Set(
        listUnitsData
          .filter(
            (unit) =>
              unit.UF?.trim().toUpperCase() ===
              selectedState.value.toUpperCase(),
          )
          .map((unit) => unit.Cidade?.trim())
          .filter((city): city is string => Boolean(city)),
      ),
    ).sort((a, b) => a.localeCompare(b, "pt-BR"));

    setCityOptions(
      cities.map((city) => ({
        label: city.toUpperCase(),
        value: city,
      })),
    );
  }, [listUnitsData, selectedState]);

  const normalizeLocation = (value: string) => {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();
};

  //apos submit do form, valida os campos e seta a cidade selecionada
  const handleSubmit = useCallback<SubmitHandler>(
    async (data) => {
      try {
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          cpf: Yup.string().required("Obrigatório"),
          celular: Yup.string().required("Obrigatório"),
          email: Yup.string().required("Obrigatório"),
        });
        await schema.validate(data, {
          abortEarly: false,
        });

        ReactPixel.fbq("track", "Lead", {
          content_name: "Lead",
        });
        ReactGA.ga("event", "conversion", {
          send_to: "AW-640319643/8U5OCIri_9ABEJuBqrEC",
        });

        const formData = [
          {
            nome: data.nome || "",
            cpf: data.cpf,
            celular: data.celular,
            email: data.email,
            cidade: "",
            estado: "",
            idUnidade: null,
            allowLocation: false,
            action: false,
          },
        ];
        dispatch(BookingIdentUserActions.setData(formData));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors],
  );

  const handleStateChange = useCallback((option: OptionTypeBase | null) => {
    setSelectedState(option);
    setCityOptions([]);
    formRef.current?.setFieldValue("city", null);
    setLocationError(null);
  }, []);

  const handleSubmitLocation = useCallback<SubmitHandler>(
    async (data) => {
      try {
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          state: Yup.string().required("Obrigatório"),
          city: Yup.string().required("Obrigatório"),
        });

        await schema.validate(data, { abortEarly: false });

        if (!bookingIdUser) {
          setLocationError("Não foi possível encontrar os dados do usuário.");
          return;
        }

        dispatch(
          BookingIdentUserActions.setData([
            {
              ...bookingIdUser,
              estado: data.state,
              cidade: data.city,
              idUnidade: null,
            },
          ]),
        );
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [bookingIdUser, dispatch, handleFormErrors],
  );

  const onSelect = useCallback(
    (unit) => {
      history.push({
        pathname: `/agendar-avaliacao/${unit.IdUnidade}`,
        state: unit,
      });
    },
    [history],
  );

  const SearchForm: React.FC = () => {
    const { loading: cpfLoading } = useSelector<RootStateOrAny, iChkCpf>(
      (rootState) => rootState.chkcpf,
    );

    return (
      <S.Wrapper>
        <S.Title>Agende a sua Avaliação</S.Title>
        <S.Text>
           Deixe a gente saber mais sobre você. Comece preenchendo seus dados no
          formulário abaixo.
        </S.Text>
        <S.Aviso>Atenção: Não temos convênio ou parceria com o SUS.</S.Aviso>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <Input type="hidden" id="nome" name="nome" />
          <InputMask
            placeholder="CPF:"
            name="cpf"
            mask="999.999.999-99"
            id="cpf"
            onBlur={(event) => handleCpfChange(event.target.value)}
            isLoading={cpfLoading}
          />
          <InputMask
            placeholder="Celular:"
            name="celular"
            mask="(99) 99999-9999"
            id="celular"
          />
          <Input name="email" id="email" placeholder="E-mail:" />

          <S.SubmitButton>
            Avançar <S.ArrowRightIcon />
          </S.SubmitButton>
        </Form>
      </S.Wrapper>
    );
  };

  const GeneralLocation: React.FC = () => (
    <S.Wrapper>
      <S.Title>Agende a sua Avaliação</S.Title>
      <S.Text>
        Comece selecionando o seu estado e a sua cidade, que iremos encontrar as
        unidades mais próximas de você.
      </S.Text>
      <S.Aviso>Atenção: Não temos convênio ou parceria com o SUS.</S.Aviso>
      <Form ref={formRef} onSubmit={handleSubmitLocation}>
        <Select
          name="state"
          label="Selecione seu estado:"
          formLabelColor="light"
          options={statesOptions}
          onChange={handleStateChange}
          defaultValue={selectedState}
        />
        <Select
          name="city"
          formLabelColor="light"
          label="Selecione sua cidade:"
          isDisabled={listUnitsLoading || cityOptions.length === 0}
          isLoading={listUnitsLoading}
          options={cityOptions}
        />
        {!listUnitsLoading && selectedState && cityOptions.length === 0 && (
          <S.Warning>
            Nenhuma cidade encontrada para o estado selecionado.
          </S.Warning>
        )}
        {locationError && <S.Warning>{locationError}</S.Warning>}
        <S.SubmitButton disabled={listUnitsLoading}>
          Agendar uma Avaliação <S.ArrowRightIcon />
        </S.SubmitButton>
      </Form>
    </S.Wrapper>
  );

  const LocationAsk: React.FC = () => (
    <S.Wrapper>
      <S.Title>Agende a sua Avaliação</S.Title>
      <S.GroupLocationAsk>
        <h2>{bookingIdUser?.nome}, deseja Permitir sua Localização Automática?</h2>
        <div>
          <button id="permitir" type="button" onClick={handleAllowLocation}>
            {isLocating ? "Obtendo localização..." : "Permitir"}
          </button>
          <button id="nao-permitir" type="button" onClick={handleDenyLocation}>
            Não permitir
          </button>
        </div>
        {locationError && <S.Warning>{locationError}</S.Warning>}
      </S.GroupLocationAsk>
    </S.Wrapper>
  );

  const handleBackToBooking = useCallback(() => {
    lastCheckedCpf.current = "";
    dispatch(BookingIdentUserActions.reset());
    dispatch(ListUnidadesActions.reset());
    // history.push("/agendar-avaliacao");
  }, [dispatch, history]);

  const Unities: React.FC = () => {
    const { data: unidadesParaMostrar } = useSelector<RootStateOrAny,ListUnidadesState>((state) => state.listUnidades);
    
    return (
     
  <S.Container>
  {unidadesParaMostrar && unidadesParaMostrar.length > 0 ? (
    <S.GroupUnities>
      <S.TitleFull>Selecione a unidade:</S.TitleFull>

      {unidadesParaMostrar.map((unit: any) => (
        <UnitCard
          key={uuidV4()}
          unit={unit}
          onSelect={onSelect}
          avaliacao={true}
        />
      ))}
    </S.GroupUnities>
  ) : (
    <S.GroupUnities>
      <h2>
        Não encontramos unidade na sua cidade.
      </h2>

      <button className="back" type="button" onClick={handleBackToBooking}>
        Voltar
      </button>
    </S.GroupUnities>
  )}
</S.Container>
    
    );
  };

  return (
    <Scaffold>
      <S.Container>
        <S.Content>
          <S.GroupScheadule>
            <div className="mleft">
              <img src={boneco} alt="Mascote" />
            </div>
            <div className="mright">
              {bookingIdUser?.action !== true ? (
                hasCompleteUserData ? (
                  <LocationAsk />
                ) : (
                  <SearchForm />
                )
              ) : bookingIdUser?.cidade === "" ? (
                <GeneralLocation/>
              ) : (
                <Unities/>
              )}
            </div>
          </S.GroupScheadule>
        </S.Content>
      </S.Container>
      <Braces />
      <Treatments />

      <Game />
      <Footer />
    </Scaffold>
  );
};
